import htmlToPdfmake from 'html-to-pdfmake';

const EMOJI_EXPORT_SIZE = 16;
const EMOJI_CANVAS_SCALE = 4;
const emojiImageCache = new Map();

const REPORT_BRAND_COLOR = '#0f4c81';
const REPORT_MUTED_COLOR = '#64748b';

const normalizePdfContent = content => {
  if (!content) return [];
  return Array.isArray(content) ? content : [content];
};

const formatReportDate = date => {
  const value = date ? new Date(date) : new Date();
  if (Number.isNaN(value.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);
};

const sanitizeFilenamePart = value => {
  return String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 80);
};

const buildReportFilename = title => {
  const safeTitle = sanitizeFilenamePart(title) || 'AI Analysis Report';
  const date = new Date().toISOString().slice(0, 10);
  return `${safeTitle} - ${date}.pdf`;
};

const blobToDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const createPdfBlob = pdfDoc =>
  new Promise((resolve, reject) => {
    try {
      pdfDoc.getBlob(blob => {
        if (!blob || !blob.size) {
          reject(new Error('Generated PDF blob is empty'));
          return;
        }
        resolve(blob);
      });
    } catch (error) {
      reject(error);
    }
  });

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30000);
};

const fetchImageAsPdfAsset = async url => {
  if (!url) return null;

  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch PDF image asset');

  const contentType = response.headers.get('content-type') || '';
  const blob = await response.blob();

  if (contentType.includes('svg')) {
    return {
      svg: await blob.text(),
      fit: [110, 36],
    };
  }

  return {
    image: await blobToDataUrl(blob),
    fit: [110, 36],
  };
};

const createReportLogo = async logoUrl => {
  if (!logoUrl) return null;

  try {
    return await fetchImageAsPdfAsset(logoUrl);
  } catch (error) {
    console.warn('Failed to prepare PDF logo:', error);
    return null;
  }
};

const getContextRows = metadata => {
  const rows = [];

  if (metadata.userName) {
    rows.push(['Created by', metadata.userName]);
  }

  if (metadata.userEmail) {
    rows.push(['Email', metadata.userEmail]);
  }

  if (metadata.projectNames?.length) {
    rows.push([
      metadata.projectNames.length > 1 ? 'Projects' : 'Project',
      metadata.projectNames.join(', '),
    ]);
  }

  if (metadata.taskNames?.length) {
    rows.push([
      metadata.taskNames.length > 1 ? 'Tasks' : 'Task',
      metadata.taskNames.join(', '),
    ]);
  }

  (metadata.quotationDetails || []).forEach(quotation => {
    const prefix = quotation.taskTitle || quotation.title || 'Task';

    if (quotation.description) {
      rows.push([`${prefix} description`, quotation.description]);
    }

    if (quotation.address) {
      rows.push([`${prefix} address`, quotation.address]);
    }

    if (quotation.contact) {
      rows.push([`${prefix} contact`, quotation.contact]);
    }
  });

  return rows;
};

const buildReportHeader = (metadata, logoAsset, useLandscapeLayout) => {
  // const title = metadata.title || 'AI Analysis Report';
  const orgName = metadata.organizationName || 'O-Hive';

  return [
    {
      columns: [
        {
          width: '*',
          stack: [
            logoAsset || {
              text: orgName,
              style: 'brandFallback',
            },
          ],
        },
        {
          width: 'auto',
          stack: [
            {
              text: formatReportDate(metadata.generatedAt),
              style: 'generatedDate',
            },
          ],
          alignment: 'right',
        },
      ],
      margin: [0, 0, 0, 18],
    },
    // {
    //   text: title,
    //   style: 'reportTitle',
    // },
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: useLandscapeLayout ? 774 : 515,
          y2: 0,
          lineWidth: 1,
          lineColor: '#dbe4ee',
        },
      ],
      margin: [0, 12, 0, 12],
    },
  ];
};

const buildReportContext = metadata => {
  const contextRows = getContextRows(metadata);
  if (!contextRows.length) return [];

  return [
    {
      table: {
        dontBreakRows: true,
        keepWithHeaderRows: 1,
        widths: ['auto', '*'],
        body: contextRows.map(([label, value]) => [
          { text: label, style: 'contextLabel' },
          { text: value, style: 'contextValue' },
        ]),
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 18,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 20],
    },
  ];
};

const escapeXml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const normalizeSvgAsset = svg => {
  if (!svg) return null;
  return String(svg)
    .replace(/<\?xml.*?\?>/, '')
    .trim();
};

const cropSvgAsset = svg => {
  const normalizedSvg = normalizeSvgAsset(svg);
  if (!normalizedSvg || typeof document === 'undefined') return normalizedSvg;

  try {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '-10000px';
    wrapper.style.visibility = 'hidden';
    wrapper.innerHTML = normalizedSvg;
    document.body.appendChild(wrapper);

    const svgElement = wrapper.querySelector('svg');
    const bbox = svgElement?.getBBox?.();
    if (!svgElement || !bbox || bbox.width <= 0 || bbox.height <= 0) {
      wrapper.remove();
      return normalizedSvg;
    }

    const padding = Math.max(bbox.width, bbox.height) * 0.04;
    const viewBox = [
      bbox.x - padding,
      bbox.y - padding,
      bbox.width + padding * 2,
      bbox.height + padding * 2,
    ];
    svgElement.setAttribute('viewBox', viewBox.join(' '));
    svgElement.setAttribute('width', String(viewBox[2]));
    svgElement.setAttribute('height', String(viewBox[3]));
    svgElement.removeAttribute('style');

    const croppedSvg = new XMLSerializer().serializeToString(svgElement);
    wrapper.remove();
    return croppedSvg;
  } catch (error) {
    console.warn('Failed to crop SVG PDF asset:', error);
    return normalizedSvg;
  }
};

const hasModelAssets = assets => {
  if (!assets) return false;
  return Boolean(
    assets.blueprintSvg ||
    assets.isometricPng ||
    assets.taskAssets?.some(
      asset =>
        asset.blueprintSvg ||
        asset.isometricPng ||
        asset.isometricSvg ||
        asset.wantsBlueprint ||
        asset.wantsIsometric,
    ),
  );
};

const keepTogether = (stack, options = {}) => ({
  stack,
  ...(options.unbreakable ? { unbreakable: true } : {}),
  margin: options.margin || [0, 0, 0, 0],
  pageBreak: options.pageBreak,
});

const createProgressDonutSvg = percent => {
  const normalizedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const stroke = (normalizedPercent / 100) * circumference;
  const progressCircle =
    normalizedPercent <= 0
      ? ''
      : normalizedPercent >= 100
        ? `<circle cx="62" cy="62" r="${radius}" fill="none" stroke="${REPORT_BRAND_COLOR}" stroke-width="13" stroke-linecap="round"/>`
        : `<circle cx="62" cy="62" r="${radius}" fill="none" stroke="${REPORT_BRAND_COLOR}" stroke-width="13"
        stroke-linecap="round" stroke-dasharray="${stroke} ${circumference - stroke}"
        transform="rotate(-90 62 62)"/>`;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="124" height="124" viewBox="0 0 124 124">
      <circle cx="62" cy="62" r="${radius}" fill="none" stroke="#e2e8f0" stroke-width="13"/>
      ${progressCircle}
      <text x="62" y="58" text-anchor="middle" font-size="24" font-weight="700" fill="#111827">${normalizedPercent}%</text>
      <text x="62" y="78" text-anchor="middle" font-size="10" fill="#64748b">complete</text>
    </svg>
  `;
};

const createDistributionSvg = (items, title, emptyLabel) => {
  const safeItems = (items || []).filter(item => item.count > 0).slice(0, 8);
  if (!safeItems.length) {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="620" height="90" viewBox="0 0 620 90">
        <rect width="620" height="90" rx="8" fill="#f8fafc" stroke="#e2e8f0"/>
        <text x="24" y="34" font-size="15" font-weight="700" fill="#111827">${escapeXml(title)}</text>
        <text x="24" y="58" font-size="11" fill="#64748b">${escapeXml(emptyLabel)}</text>
      </svg>
    `;
  }

  const rowHeight = 26;
  const height = 48 + safeItems.length * rowHeight;
  const maxCount = Math.max(...safeItems.map(item => item.count), 1);
  const rows = safeItems
    .map((item, index) => {
      const y = 42 + index * rowHeight;
      const ratio = Number.isFinite(item.percent)
        ? Math.max(0, Math.min(100, item.percent)) / 100
        : item.count / maxCount;
      const barWidth = Math.max(6, ratio * 350);
      const color = item.color || REPORT_BRAND_COLOR;
      return `
        <text x="24" y="${y + 12}" font-size="11" fill="#334155">${escapeXml(item.label || item.name)}</text>
        <rect x="190" y="${y}" width="350" height="12" rx="6" fill="#e2e8f0"/>
        <rect x="190" y="${y}" width="${barWidth}" height="12" rx="6" fill="${escapeXml(color)}"/>
        <text x="558" y="${y + 10}" font-size="10" fill="#475569">${item.count}</text>
      `;
    })
    .join('');

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="620" height="${height}" viewBox="0 0 620 ${height}">
      <rect width="620" height="${height}" rx="8" fill="#ffffff" stroke="#dbe4ee"/>
      <text x="24" y="26" font-size="15" font-weight="700" fill="#111827">${escapeXml(title)}</text>
      ${rows}
    </svg>
  `;
};

const buildStatusBreakdownTable = stats => {
  const statuses = stats.statusDistribution || [];
  if (!statuses.length) return [];

  return [
    {
      table: {
        dontBreakRows: true,
        keepWithHeaderRows: 1,
        widths: ['*', 55, 65],
        body: [
          [
            { text: 'Status', style: 'tableHeader' },
            { text: 'Tasks', style: 'tableHeader', alignment: 'right' },
            { text: 'Share', style: 'tableHeader', alignment: 'right' },
          ],
          ...statuses.map(status => [
            {
              columns: [
                {
                  width: 8,
                  canvas: [
                    {
                      type: 'rect',
                      x: 0,
                      y: 4,
                      w: 8,
                      h: 8,
                      r: 2,
                      color: status.color || REPORT_BRAND_COLOR,
                    },
                  ],
                },
                {
                  width: '*',
                  text: status.label || 'No status',
                  style: 'tableCell',
                  margin: [6, 0, 0, 0],
                },
              ],
            },
            {
              text: String(status.count),
              style: 'tableCell',
              alignment: 'right',
            },
            {
              text: `${status.percent || 0}%`,
              style: 'tableCell',
              alignment: 'right',
            },
          ]),
        ],
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 14],
    },
  ];
};

const buildAssigneeWorkloadTable = stats => {
  const assignees = (stats.assignees || []).slice(0, 12);
  if (!assignees.length) {
    return [
      {
        text: 'No assignees are assigned to the selected tasks.',
        style: 'mutedText',
        margin: [0, 0, 0, 18],
      },
    ];
  }

  const statuses = (stats.statusDistribution || []).slice(0, 5);
  const body = [
    [
      { text: 'Assignee', style: 'tableHeader' },
      { text: 'Total', style: 'tableHeader', alignment: 'right' },
      ...statuses.map(status => ({
        text: status.label || 'No status',
        style: 'tableHeader',
        alignment: 'right',
      })),
    ],
    ...assignees.map(assignee => [
      { text: assignee.name || 'Unassigned', style: 'tableCell' },
      {
        text: String(assignee.count || 0),
        style: 'tableCell',
        alignment: 'right',
      },
      ...statuses.map(status => ({
        text: String(assignee.statuses?.[status.key] || 0),
        style: 'tableCell',
        alignment: 'right',
      })),
    ]),
  ];

  return [
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        keepWithHeaderRows: 1,
        widths: ['*', 42, ...statuses.map(() => 54)],
        body,
      },
      layout: {
        hLineColor: () => '#e2e8f0',
        vLineColor: () => '#e2e8f0',
        hLineWidth: index => (index === 0 || index === 1 ? 1 : 0.5),
        vLineWidth: () => 0,
        fillColor: (rowIndex, node, columnIndex) => {
          if (rowIndex === 0) return '#f1f5f9';
          if (columnIndex === 1) return '#f8fafc';
          return rowIndex % 2 === 0 ? '#fbfdff' : null;
        },
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 6,
        paddingBottom: () => 6,
      },
      margin: [0, 0, 0, 20],
    },
  ];
};

const buildReportStatsSection = stats => {
  if (!stats?.totalTasks) return [];

  const assigneeItems = (stats.assignees || []).map(assignee => ({
    ...assignee,
    label: assignee.name,
    color: '#22a06b',
  }));

  return [
    keepTogether(
      [
        {
          text: 'Task Progress',
          style: 'sectionTitle',
          headlineLevel: 1,
          margin: [0, 0, 0, 8],
        },
        {
          columns: [
            {
              width: 122,
              svg: createProgressDonutSvg(stats.completionPercent),
              fit: [112, 112],
            },
            {
              width: '*',
              stack: [
                {
                  text: `${stats.completedTasks} of ${stats.totalTasks} tasks completed`,
                  style: 'metricValue',
                  margin: [0, 12, 0, 4],
                },
                {
                  text: 'Progress and assignee metrics are based on the selected report task scope.',
                  style: 'mutedText',
                },
              ],
            },
          ],
          columnGap: 18,
        },
      ],
      { margin: [0, 0, 0, 14] },
    ),
    keepTogether(
      [
        {
          svg: createDistributionSvg(
            stats.statusDistribution,
            'Status Distribution',
            'No status values available for the selected tasks.',
          ),
          fit: [515, 190],
          margin: [0, 0, 0, 10],
        },
        ...buildStatusBreakdownTable(stats),
      ],
      { margin: [0, 0, 0, 2] },
    ),
    keepTogether(
      [
        {
          text: 'Assignee Workload',
          style: 'assetTitle',
          headlineLevel: 2,
          margin: [0, 2, 0, 8],
        },
        ...buildAssigneeWorkloadTable({
          ...stats,
          assignees: assigneeItems,
        }),
      ],
      { margin: [0, 0, 0, 8] },
    ),
  ];
};

const preparePdfTaskAssets = async assets => {
  if (!assets?.taskAssets?.length) return assets;

  const taskAssets = await Promise.all(
    assets.taskAssets.map(async asset => {
      if (!asset.isometricUrl || asset.isometricPng || asset.isometricSvg) {
        return asset;
      }

      try {
        const pdfAsset = await fetchImageAsPdfAsset(asset.isometricUrl);
        return {
          ...asset,
          isometricPng: pdfAsset?.image || null,
          isometricSvg: pdfAsset?.svg || null,
        };
      } catch (error) {
        console.warn('Failed to prepare task model thumbnail for PDF:', error);
        return asset;
      }
    }),
  );

  return {
    ...assets,
    taskAssets,
  };
};

const createAssetImageNode = asset => {
  if (asset.svg) {
    return {
      svg: cropSvgAsset(asset.svg),
      fit: asset.fit || [760, 470],
      alignment: 'center',
    };
  }

  if (asset.image) {
    return {
      image: asset.image,
      fit: asset.fit || [760, 470],
      alignment: 'center',
    };
  }

  return {
    text: 'Not available',
    style: 'mutedText',
    margin: [0, 62, 0, 0],
    alignment: 'center',
  };
};

const buildReportAssetsSection = assets => {
  const blueprintSvg = cropSvgAsset(assets?.blueprintSvg);
  const isometricPng = assets?.isometricPng;
  const taskAssets = assets?.taskAssets || [];
  if (!blueprintSvg && !isometricPng && !taskAssets.length) return [];

  const content = [
    {
      text: 'Model Assets',
      style: 'sectionTitle',
      headlineLevel: 1,
      margin: [0, 2, 0, 8],
    },
  ];

  if (blueprintSvg) {
    content.push(
      keepTogether(
        [
          {
            text: 'Blueprint',
            style: 'assetTitle',
            headlineLevel: 2,
            margin: [0, 0, 0, 6],
          },
          {
            svg: blueprintSvg,
            fit: [760, 470],
            alignment: 'center',
          },
        ],
        { margin: [0, 0, 0, 24] },
      ),
    );
  }

  if (isometricPng) {
    content.push(
      keepTogether(
        [
          {
            text: 'Isometric View',
            style: 'assetTitle',
            headlineLevel: 2,
            margin: [0, 0, 0, 6],
          },
          {
            image: isometricPng,
            fit: [760, 470],
            alignment: 'center',
          },
        ],
        { margin: [0, 0, 0, 26] },
      ),
    );
  }

  taskAssets.forEach(asset => {
    const taskBlueprintSvg = cropSvgAsset(asset.blueprintSvg);
    const hasBlueprint = Boolean(taskBlueprintSvg);
    const hasIsometric = Boolean(asset.isometricPng || asset.isometricSvg);
    const wantsBlueprint = Boolean(asset.wantsBlueprint);
    const wantsIsometric = Boolean(asset.wantsIsometric);

    if (!hasBlueprint && !hasIsometric && !wantsBlueprint && !wantsIsometric) {
      return;
    }

    if (wantsBlueprint || hasBlueprint) {
      content.push(
        keepTogether(
          [
            {
              text: asset.taskName || 'Task model',
              style: 'assetTitle',
              headlineLevel: 2,
              margin: [0, 2, 0, 8],
            },
            {
              text: 'Blueprint',
              style: 'contextLabel',
              margin: [0, 0, 0, 5],
            },
            createAssetImageNode({
              svg: taskBlueprintSvg,
              fit: [760, 450],
            }),
          ],
          { margin: [0, 0, 0, 22] },
        ),
      );
    }

    if (wantsIsometric || hasIsometric) {
      content.push(
        keepTogether(
          [
            {
              text:
                wantsBlueprint || hasBlueprint
                  ? `${asset.taskName || 'Task model'} - Isometric`
                  : asset.taskName || 'Task model',
              style: 'assetTitle',
              headlineLevel: 2,
              margin: [0, 2, 0, 8],
            },
            {
              text: 'Isometric View',
              style: 'contextLabel',
              margin: [0, 0, 0, 5],
            },
            createAssetImageNode({
              image: asset.isometricPng,
              svg: asset.isometricSvg,
              fit: [760, 450],
            }),
          ],
          { margin: [0, 0, 0, 26] },
        ),
      );
    }
  });

  return content;
};

const parseChartSpec = value => {
  if (!value) return null;

  try {
    const spec = JSON.parse(value);
    if (!spec || !Array.isArray(spec.labels)) return null;

    const datasets = Array.isArray(spec.datasets)
      ? spec.datasets
      : [{ label: spec.label || '', data: spec.data || [] }];

    const normalizedDatasets = datasets
      .map(dataset => ({
        label: dataset.label || '',
        data: Array.isArray(dataset.data)
          ? dataset.data.map(Number).filter(Number.isFinite)
          : [],
      }))
      .filter(dataset => dataset.data.length);

    if (!normalizedDatasets.length) return null;

    return {
      type: ['line', 'bar'].includes(spec.type) ? spec.type : 'bar',
      title: spec.title || '',
      labels: spec.labels.map(label => String(label)),
      datasets: normalizedDatasets,
    };
  } catch (error) {
    console.warn('Invalid chart block skipped in PDF export:', error);
    return null;
  }
};

const createChartSvg = spec => {
  const width = 620;
  const height = 340;
  const padding = {
    top: spec.title ? 46 : 26,
    right: 26,
    bottom: 64,
    left: 58,
  };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const colors = ['#0f4c81', '#22a06b', '#c2410c', '#7c3aed'];
  const allValues = spec.datasets.flatMap(dataset => dataset.data);
  const minValue = Math.min(0, ...allValues);
  const maxValue = Math.max(1, ...allValues);
  const valueRange = maxValue - minValue || 1;
  const xStep = plotWidth / Math.max(spec.labels.length, 1);
  const escapeXml = value =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  const yForValue = value =>
    padding.top + plotHeight - ((value - minValue) / valueRange) * plotHeight;
  const labelEvery = Math.max(1, Math.ceil(spec.labels.length / 8));

  const gridLines = Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    const y = padding.top + plotHeight - ratio * plotHeight;
    const value = minValue + ratio * valueRange;
    return `
      <line x1="${padding.left}" y1="${y}" x2="${width - padding.right}" y2="${y}" stroke="#e2e8f0" stroke-width="1"/>
      <text x="${padding.left - 10}" y="${y + 4}" text-anchor="end" font-size="10" fill="#64748b">${Number(value.toFixed(2))}</text>
    `;
  }).join('');

  const xLabels = spec.labels
    .map((label, index) => {
      if (index % labelEvery !== 0) return '';
      const x = padding.left + xStep * index + xStep / 2;
      return `<text x="${x}" y="${height - 28}" text-anchor="middle" font-size="10" fill="#64748b">${escapeXml(label)}</text>`;
    })
    .join('');

  const series =
    spec.type === 'line'
      ? spec.datasets
          .map((dataset, datasetIndex) => {
            const points = dataset.data
              .map((value, index) => {
                const x = padding.left + xStep * index + xStep / 2;
                return `${x},${yForValue(value)}`;
              })
              .join(' ');
            return `
              <polyline points="${points}" fill="none" stroke="${colors[datasetIndex % colors.length]}" stroke-width="3"/>
              ${dataset.data
                .map((value, index) => {
                  const x = padding.left + xStep * index + xStep / 2;
                  return `<circle cx="${x}" cy="${yForValue(value)}" r="3" fill="${colors[datasetIndex % colors.length]}"/>`;
                })
                .join('')}
            `;
          })
          .join('')
      : spec.datasets
          .map((dataset, datasetIndex) => {
            const groupWidth = xStep * 0.72;
            const barWidth = groupWidth / spec.datasets.length;
            const groupOffset = (xStep - groupWidth) / 2;
            return dataset.data
              .map((value, index) => {
                const x =
                  padding.left +
                  xStep * index +
                  groupOffset +
                  barWidth * datasetIndex;
                const y = yForValue(Math.max(value, 0));
                const zeroY = yForValue(0);
                const barHeight = Math.max(1, Math.abs(zeroY - y));
                return `<rect x="${x}" y="${Math.min(y, zeroY)}" width="${Math.max(1, barWidth - 3)}" height="${barHeight}" fill="${colors[datasetIndex % colors.length]}"/>`;
              })
              .join('');
          })
          .join('');

  const legend = spec.datasets
    .map((dataset, index) => {
      const x = padding.left + index * 130;
      const y = height - 10;
      return `
        <rect x="${x}" y="${y - 9}" width="9" height="9" fill="${colors[index % colors.length]}"/>
        <text x="${x + 14}" y="${y}" font-size="10" fill="#334155">${escapeXml(dataset.label)}</text>
      `;
    })
    .join('');

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="${width}" height="${height}" rx="6" fill="#ffffff" stroke="#dbe4ee"/>
      ${spec.title ? `<text x="${padding.left}" y="26" font-size="16" font-weight="700" fill="#111827">${escapeXml(spec.title)}</text>` : ''}
      ${gridLines}
      <line x1="${padding.left}" y1="${padding.top}" x2="${padding.left}" y2="${padding.top + plotHeight}" stroke="#94a3b8"/>
      <line x1="${padding.left}" y1="${padding.top + plotHeight}" x2="${width - padding.right}" y2="${padding.top + plotHeight}" stroke="#94a3b8"/>
      ${series}
      ${xLabels}
      ${legend}
    </svg>
  `;
};

const replaceChartBlocksWithSvg = root => {
  const chartBlocks = root.querySelectorAll(
    'pre code.language-chart, pre code.language-graph',
  );

  chartBlocks.forEach(code => {
    const spec = parseChartSpec(code.textContent);
    if (!spec) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = createChartSvg(spec).trim();
    const svg = wrapper.firstElementChild;
    code.closest('pre')?.replaceWith(svg);
  });
};

const createEmojiImageFragment = emoji => ({
  image: renderEmojiToDataUrl(emoji),
  width: EMOJI_EXPORT_SIZE * 0.72,
  height: EMOJI_EXPORT_SIZE * 0.72,
  margin: [0, 0, 1, -2],
});

const splitTextWithEmojiImages = text => {
  if (typeof text !== 'string') return text;

  const segments = getTextSegments(text);
  if (!segments.some(isEmojiSegment)) return text;

  const fragments = [];
  let textBuffer = '';

  const flushText = () => {
    if (!textBuffer) return;
    fragments.push({ text: textBuffer });
    textBuffer = '';
  };

  segments.forEach(segment => {
    if (!isEmojiSegment(segment)) {
      textBuffer += segment;
      return;
    }

    flushText();
    fragments.push(createEmojiImageFragment(segment));
  });

  flushText();
  return fragments;
};

const replaceEmojisInPdfContent = content => {
  if (Array.isArray(content)) {
    content.forEach(replaceEmojisInPdfContent);
    return content;
  }

  if (!content || typeof content !== 'object') return content;

  if (typeof content.text === 'string') {
    content.text = splitTextWithEmojiImages(content.text);
  } else if (Array.isArray(content.text)) {
    content.text.forEach(replaceEmojisInPdfContent);
  }

  ['stack', 'columns', 'ul', 'ol'].forEach(key => {
    if (Array.isArray(content[key])) {
      content[key].forEach(replaceEmojisInPdfContent);
    }
  });

  if (content.table?.body) {
    content.table.body.forEach(row => {
      row.forEach(cell => replaceEmojisInPdfContent(cell));
    });
  }

  return content;
};

const getNodeStyles = node => {
  if (!node?.style) return [];
  return Array.isArray(node.style) ? node.style : [node.style];
};

const applyPdfLayoutGuards = content => {
  if (Array.isArray(content)) {
    content.forEach(applyPdfLayoutGuards);
    return content;
  }

  if (!content || typeof content !== 'object') return content;

  const styles = getNodeStyles(content);
  const headingStyle = styles.find(style => /^html-h[1-6]$/.test(style));
  if (headingStyle) {
    content.headlineLevel = Number(headingStyle.replace('html-h', '')) || 2;
    content.margin = content.margin || [0, 8, 0, 6];
  }

  if (content.table?.body?.length) {
    content.table.dontBreakRows = true;
    content.table.keepWithHeaderRows = 1;
    content.table.headerRows = content.table.headerRows || 1;
    content.margin = content.margin || [0, 6, 0, 12];
    content.layout = content.layout || {
      hLineColor: () => '#e2e8f0',
      vLineColor: () => '#e2e8f0',
      hLineWidth: index => (index === 0 || index === 1 ? 1 : 0.5),
      vLineWidth: () => 0,
      fillColor: rowIndex => (rowIndex === 0 ? '#f8fafc' : null),
      paddingLeft: () => 7,
      paddingRight: () => 7,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    };
  }

  ['stack', 'columns', 'ul', 'ol'].forEach(key => {
    if (Array.isArray(content[key])) {
      content[key].forEach(applyPdfLayoutGuards);
    }
  });

  if (content.table?.body) {
    content.table.body.forEach(row => {
      if (Array.isArray(row)) {
        row.forEach(cell => applyPdfLayoutGuards(cell));
      }
    });
  }

  return content;
};

const getTextSegments = text => {
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    return Array.from(
      new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text),
      segment => segment.segment,
    );
  }

  return Array.from(text);
};

const isEmojiSegment = segment => {
  if (!segment || /^[0-9#*]$/.test(segment)) return false;

  return (
    /\p{Extended_Pictographic}/u.test(segment) ||
    /\p{Emoji_Presentation}/u.test(segment) ||
    /\uFE0F|\u20E3/.test(segment)
  );
};

const renderEmojiToDataUrl = emoji => {
  if (emojiImageCache.has(emoji)) return emojiImageCache.get(emoji);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const fontSize = EMOJI_EXPORT_SIZE * EMOJI_CANVAS_SCALE;
  const fontFamily =
    '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

  context.font = `${fontSize}px ${fontFamily}`;
  const measuredWidth = context.measureText(emoji).width;
  const canvasSize = Math.ceil(
    Math.max(fontSize * 1.25, measuredWidth + EMOJI_CANVAS_SCALE * 4),
  );

  canvas.width = canvasSize;
  canvas.height = canvasSize;

  context.font = `${fontSize}px ${fontFamily}`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(emoji, canvasSize / 2, canvasSize / 2);

  const dataUrl = canvas.toDataURL('image/png');
  emojiImageCache.set(emoji, dataUrl);
  return dataUrl;
};

export const handleDownloadPdf = async (htmlContentId, options = {}) => {
  try {
    const [pdfMakeModule] = await Promise.all([
      import('pdfmake/build/pdfmake.js'),
    ]);

    const pdfMake = pdfMakeModule.default;

    // Dynamically fetch font files from static folder and inject into pdfMake.vfs
    const fontUrls = {
      normal: '/static/fonts/NotoSansKR-Regular.ttf',
      bold: '/static/fonts/NotoSansKR-Bold.ttf',
    };

    // Helper to fetch and convert font to base64
    const fetchFontBase64 = async url => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch font: ${url}`);
      const buffer = await response.arrayBuffer();
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    };

    // Load fonts in parallel
    const [fontNormal, fontBold] = await Promise.all([
      fetchFontBase64(fontUrls.normal),
      fetchFontBase64(fontUrls.bold),
    ]);

    pdfMake.vfs = pdfMake.vfs || {};
    pdfMake.vfs['NotoSansKR-Regular.ttf'] = fontNormal;
    pdfMake.vfs['NotoSansKR-Bold.ttf'] = fontBold;

    pdfMake.fonts = {
      NotoSansKR: {
        normal: 'NotoSansKR-Regular.ttf',
        bold: 'NotoSansKR-Bold.ttf',
      },
    };

    const element = document.getElementById(htmlContentId);
    if (!element) {
      throw new Error('HTML element not found');
    }

    // Clone the element to avoid modifying the original DOM
    const clonedElement = element.cloneNode(true);

    // Remove all copy-table-button elements
    const copyButtons = clonedElement.querySelectorAll('.copy-table-button');
    copyButtons.forEach(button => button.remove());

    const htmlString = clonedElement.innerHTML;

    if (!htmlString) {
      throw new Error('HTML content is empty');
    }

    const convertedElement = document.createElement('div');
    convertedElement.innerHTML = htmlString;
    replaceChartBlocksWithSvg(convertedElement);

    const metadata = {
      title: options.title || 'AI Analysis Report',
      organizationName: options.organizationName,
      userName: options.userName,
      userEmail: options.userEmail,
      taskNames: options.taskNames || [],
      projectNames: options.projectNames || [],
      quotationDetails: options.quotationDetails || [],
      generatedAt: options.generatedAt || new Date(),
    };
    const logoAsset = await createReportLogo(options.logoUrl);
    const pdfAssets = await preparePdfTaskAssets(options.assets);
    const useLandscapeLayout = hasModelAssets(pdfAssets);
    const reportContent = normalizePdfContent(
      htmlToPdfmake(convertedElement.innerHTML),
    );
    replaceEmojisInPdfContent(reportContent);
    applyPdfLayoutGuards(reportContent);

    const docDefinition = {
      pageOrientation: useLandscapeLayout ? 'landscape' : 'portrait',
      pageMargins: useLandscapeLayout ? [34, 34, 34, 48] : [40, 44, 40, 64],
      content: [
        ...buildReportHeader(metadata, logoAsset, useLandscapeLayout),
        ...buildReportContext(metadata),
        ...buildReportStatsSection(options.reportStats),
        ...buildReportAssetsSection(pdfAssets),
        {
          stack: reportContent,
          style: 'reportBody',
        },
      ],
      footer: (currentPage, pageCount) => ({
        columns: [
          {
            text: metadata.organizationName || 'AI Analysis Report',
            style: 'footerText',
          },
          {
            text: `${currentPage} / ${pageCount}`,
            style: 'footerText',
            alignment: 'right',
          },
        ],
        margin: [40, 18, 40, 0],
      }),
      defaultStyle: {
        font: 'NotoSansKR',
        fontSize: 10,
        lineHeight: 1.25,
        color: '#1f2937',
      },
      styles: {
        brandFallback: {
          font: 'NotoSansKR',
          fontSize: 18,
          bold: true,
          color: REPORT_BRAND_COLOR,
        },
        documentLabel: {
          font: 'NotoSansKR',
          fontSize: 9,
          bold: true,
          color: REPORT_BRAND_COLOR,
          characterSpacing: 0.5,
        },
        generatedDate: {
          font: 'NotoSansKR',
          fontSize: 8,
          color: REPORT_MUTED_COLOR,
          margin: [0, 3, 0, 0],
        },
        reportTitle: {
          font: 'NotoSansKR',
          fontSize: 22,
          bold: true,
          color: '#111827',
          margin: [0, 0, 0, 0],
        },
        contextLabel: {
          font: 'NotoSansKR',
          fontSize: 8,
          bold: true,
          color: REPORT_MUTED_COLOR,
        },
        contextValue: {
          font: 'NotoSansKR',
          fontSize: 9,
          color: '#111827',
        },
        reportBody: {
          font: 'NotoSansKR',
          fontSize: 10,
          color: '#1f2937',
          lineHeight: 1.3,
        },
        sectionTitle: {
          font: 'NotoSansKR',
          fontSize: 15,
          bold: true,
          color: '#111827',
        },
        assetTitle: {
          font: 'NotoSansKR',
          fontSize: 11,
          bold: true,
          color: '#334155',
        },
        metricValue: {
          font: 'NotoSansKR',
          fontSize: 16,
          bold: true,
          color: '#111827',
        },
        mutedText: {
          font: 'NotoSansKR',
          fontSize: 9,
          color: REPORT_MUTED_COLOR,
          lineHeight: 1.3,
        },
        tableHeader: {
          font: 'NotoSansKR',
          fontSize: 8,
          bold: true,
          color: '#475569',
        },
        tableCell: {
          font: 'NotoSansKR',
          fontSize: 8.5,
          color: '#111827',
        },
        header: {
          font: 'NotoSansKR',
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        footerText: {
          font: 'NotoSansKR',
          fontSize: 8,
          color: '#94a3b8',
        },
      },
    };

    const filename = options.filename || buildReportFilename(metadata.title);
    const pdfDoc = pdfMake.createPdf(
      docDefinition,
      null,
      pdfMake.fonts,
      pdfMake.vfs,
    );
    const pdfBlob = await createPdfBlob(pdfDoc);
    downloadBlob(pdfBlob, filename);
  } catch (error) {
    console.error('Error generating PDF with pdfmake:', error);
    throw error;
  }
};
